/**
 * Credit usage for the logged-in user (PRD planning credits, ×5 scale).
 * Data from GET /api/credit-usage on the Sales API.
 */

import React, { useEffect, useState } from "react";
import { Coins, RefreshCw, AlertCircle } from "lucide-react";
import { agentApi } from "../services/api";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

const CreditUsagePanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCredits, setTotalCredits] = useState(0);
  const [byAction, setByAction] = useState([]);
  const [events, setEvents] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await agentApi.get("/api/credit-usage", { params: { limit: 200 } });
      if (data.success) {
        setTotalCredits(data.totalCredits ?? 0);
        setByAction(Array.isArray(data.byAction) ? data.byAction : []);
        setEvents(Array.isArray(data.events) ? data.events : []);
      } else {
        setError(data.message || "Failed to load");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load credit usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-freshroad-off-white p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Coins className="w-6 h-6 text-freshroad-teal" />
              Credit usage
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">
              Totals reflect AI-related actions on your account (chatbot, knowledge base uploads,
              analytics, voice calls). Voice calls are billed per minute at a bundled rate (connect, in-call
              AI, TTS, and post-call analysis together). Standalone Kokoro preview uses a separate line.
              <br />
              <span className="block mt-2 font-medium text-gray-900">1 credit = $0.0001 USD</span>
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-sm font-medium text-gray-500">All-time total (your account)</div>
            <div className="text-4xl font-bold text-freshroad-teal mt-2 tabular-nums">
              {loading ? "…" : totalCredits.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 mt-1 tabular-nums">
              ≈ ${(loading || totalCredits === 0 ? "0.00" : (totalCredits * 0.0001).toFixed(2))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">
            By action type
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : byAction.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              No recorded usage yet. Use the chatbot, upload knowledge base files, run analytics, or
              place calls with analysis to see credits here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-2 font-medium">Action</th>
                    <th className="px-4 py-2 font-medium text-right">Events</th>
                    <th className="px-4 py-2 font-medium text-right">Credits</th>
                    <th className="px-4 py-2 font-medium text-right">USD (~)</th>
                  </tr>
                </thead>
                <tbody>
                  {byAction.map((row) => (
                    <tr key={row.action} className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-900">{row.label || row.action}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">{row.count}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-900">
                        {(row.credits || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        ${((row.credits || 0) * 0.0001).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">
            Recent events
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No events yet.</div>
          ) : (
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-left text-gray-600">
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-4 py-2 font-medium">Action</th>
                    <th className="px-4 py-2 font-medium text-right">Credits</th>
                    <th className="px-4 py-2 font-medium text-right">USD (~)</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatWhen(ev.createdAt)}</td>
                      <td className="px-4 py-2 text-gray-900">{ev.label || ev.action}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {(ev.credits ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        ${((ev.credits ?? 0) * 0.0001).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditUsagePanel;
